/**
 * Eén bron van waarheid voor per-route SEO-metadata (title, description, H1).
 * Wordt gebruikt door ZOWEL de server (server/seo-meta.ts, SSR) als de client
 * (client/src/hooks/use-seo.ts). Wijzig titles/descriptions uitsluitend hier —
 * dan blijven view-source en document.title na hydration gegarandeerd gelijk.
 *
 * Richtlijnen: title ≤ ~60 tekens, description ≤ ~155 tekens.
 */

export interface RouteSeoEntry {
  title: string;
  description: string;
  h1: string;
}

export const routeSeo: Record<string, RouteSeoEntry> = {
  "/": {
    title: "Online Marketing Bureau Amsterdam voor D2C | Wrkt Digital",
    description:
      "Online marketing bureau in Amsterdam voor D2C e-commerce. AI-gedreven Meta Ads, TikTok Ads, Google Ads en Klaviyo — data-driven groei voor webshops.",
    h1: "Online marketing bureau Amsterdam voor D2C e-commerce groei",
  },
  "/about": {
    title: "Over Ons | Wrkt Digital - AI Marketing Amsterdam",
    description:
      "Maak kennis met Wrkt Digital: senior team, 15+ jaar ervaring, 0 account managers. AI-driven digital marketing bureau Amsterdam.",
    h1: "Over Wrkt Digital — AI Marketing Specialisten Amsterdam",
  },
  "/werkwijze": {
    title: "Werkwijze | Wrkt Digital - Growth Compass Methode",
    description:
      "Ontdek de Growth Compass methode: 4 fasen van Intelligence, Creation, Execution tot Retention. Data-driven groei voor D2C merken.",
    h1: "Werkwijze — Data-Driven Growth Methode voor D2C Merken",
  },
  "/cases": {
    title: "Cases & Resultaten | Wrkt Digital - D2C Marketing",
    description:
      "Bekijk bewezen resultaten van Wrkt Digital voor D2C e-commerce merken. Cases van Vedder & Vedder, Franky Amsterdam, Feather Women en meer.",
    h1: "Cases & Resultaten — D2C E-commerce Marketing",
  },
  "/scan": {
    title: "Gratis Growth Scan | Wrkt Digital",
    description:
      "Gratis Growth Scan: analyseer je website op SEO, social media, tracking, performance en advertising. Direct inzicht in groeikansen.",
    h1: "Gratis Growth Scan — Website Analyse voor E-commerce",
  },
  "/contact": {
    title: "Contact | Wrkt Digital - Digital Marketing Bureau Amsterdam",
    description:
      "Neem contact op met Wrkt Digital, digital marketing bureau in Amsterdam. Praat direct met Robin de Wildt — geen salesteam, geen wachtrij.",
    h1: "Contact — Praat Direct met Robin, Niet met Sales",
  },
  "/artikelen": {
    title: "Artikelen | Wrkt Digital - Inzichten voor D2C",
    description:
      "Praktische tips en strategieën voor D2C e-commerce groei. Van Meta Ads en TikTok tot data-driven branding en Klaviyo email marketing.",
    h1: "Artikelen — Inzichten voor D2C E-commerce Groei",
  },
  "/sitemap": {
    title: "Sitemap | Wrkt Digital - Alle Pagina's",
    description:
      "Overzicht van alle pagina's op wrkt.nl. Navigeer naar cases, artikelen, diensten en meer.",
    h1: "Sitemap — Wrkt Digital",
  },
  "/privacy": {
    title: "Privacybeleid | Wrkt Digital",
    description:
      "Privacybeleid van Wrkt Digital BV. Hoe wij omgaan met je persoonsgegevens, cookies, analytics en tracking op wrkt.nl.",
    h1: "Privacybeleid — Wrkt Digital BV",
  },
  "/growth-marketing-d2c": {
    title: "Growth Marketing Bureau voor D2C | Wrkt Digital",
    description:
      "Growth marketing bureau voor D2C e-commerce in Nederland. Van paid social tot lifecycle marketing — geïntegreerde aanpak voor duurzame groei.",
    h1: "Growth Marketing voor D2C Merken: van eerste aankoop naar duurzame groei",
  },
  "/meta-ads-bureau-amsterdam": {
    title: "Meta Ads Bureau Amsterdam voor D2C E-commerce | Wrkt Digital",
    description:
      "Meta Ads bureau in Amsterdam voor D2C e-commerce. AI-gedreven Facebook- en Instagram-campagnes met bewezen creative frameworks.",
    h1: "Meta Ads voor D2C E-commerce: adverteren dat schaalt",
  },
  "/tiktok-ads-bureau": {
    title: "TikTok Ads Bureau voor E-commerce | Wrkt Digital",
    description:
      "TikTok Ads bureau voor D2C e-commerce. AI-gedreven creative strategie, hook frameworks en campagnebeheer voor webshops met ambitie.",
    h1: "TikTok Ads voor E-commerce: het kanaal dat je concurrenten nog onderschatten",
  },
  "/ai-marketing-bureau": {
    title: "AI Marketing Bureau voor D2C E-commerce | Wrkt Digital",
    description:
      "AI marketing bureau voor D2C e-commerce. Eigen AI-tooling wrkt signal analyseert je markt en optimaliseert campagnes 24/7.",
    h1: "AI marketing bureau voor D2C e-commerce",
  },
  "/signal": {
    title: "Wrkt Signal: Competitive Ad Intelligence voor D2C | Wrkt Digital",
    description:
      "Wrkt Signal is de eigen AI-tool van Wrkt Digital: honderden concurrent-ads per scan, geclassificeerd op angle en format, vertaald naar concrete testkansen.",
    h1: "Wrkt Signal: competitive ad intelligence voor D2C e-commerce",
  },
  "/klaviyo-email-marketing-ecommerce": {
    title: "Klaviyo Bureau voor D2C E-commerce | Wrkt Digital",
    description:
      "Gespecialiseerd in Klaviyo en e-mailmarketing voor D2C e-commerce. Flows, segmentatie en retentiestrategieën die je omzet structureel verhogen.",
    h1: "Klaviyo voor D2C E-commerce: e-mail marketing die omzet oplevert",
  },

  "/en": {
    title: "Wrkt Digital | AI-Driven D2C Marketing Agency Amsterdam",
    description:
      "Wrkt Digital — AI-driven digital marketing agency Amsterdam. Performance marketing, Meta Ads, TikTok Ads, Pinterest Ads & Klaviyo for D2C e-commerce brands.",
    h1: "AI-Driven Digital Marketing Agency Amsterdam",
  },
  "/en/about": {
    title: "About Us | Wrkt Digital - AI Marketing Amsterdam",
    description:
      "Meet Wrkt Digital: senior team, 15+ years experience, 0 account managers. AI-driven digital marketing agency Amsterdam.",
    h1: "About Wrkt Digital — AI Marketing Specialists Amsterdam",
  },
  "/en/werkwijze": {
    title: "Method | Wrkt Digital - Growth Compass Method",
    description:
      "Discover the Growth Compass method: 4 phases from Intelligence, Creation, Execution to Retention. Data-driven growth for D2C brands.",
    h1: "Method — Data-Driven Growth for D2C Brands",
  },
  "/en/cases": {
    title: "Cases & Results | Wrkt Digital - D2C Marketing",
    description:
      "View proven results from Wrkt Digital for D2C e-commerce brands. Cases from Vedder & Vedder, Franky Amsterdam, Feather Women and more.",
    h1: "Cases & Results — D2C E-commerce Marketing",
  },
  "/en/scan": {
    title: "Free Growth Scan | Wrkt Digital",
    description:
      "Free Growth Scan: analyze your website on SEO, social media, tracking, performance and advertising. Instant growth insights.",
    h1: "Free Growth Scan — Website Analysis for E-commerce",
  },
  "/en/contact": {
    title: "Contact | Wrkt Digital - Digital Marketing Agency Amsterdam",
    description:
      "Contact Wrkt Digital, digital marketing agency in Amsterdam. Talk directly to Robin de Wildt — no sales team, no waiting line.",
    h1: "Contact — Talk Directly to Robin, Not Sales",
  },
  "/en/artikelen": {
    title: "Articles | Wrkt Digital - Insights for D2C",
    description:
      "Practical tips and strategies for D2C e-commerce growth. From Meta Ads and TikTok to data-driven branding and Klaviyo email marketing.",
    h1: "Articles — Insights for D2C E-commerce Growth",
  },
  "/en/privacy": {
    title: "Privacy Policy | Wrkt Digital",
    description:
      "Privacy Policy of Wrkt Digital BV. How we handle your personal data, cookies, analytics and tracking on wrkt.nl.",
    h1: "Privacy Policy — Wrkt Digital BV",
  },
  "/en/growth-marketing-d2c": {
    title: "Growth Marketing Agency for D2C | Wrkt Digital",
    description:
      "Growth marketing agency for D2C e-commerce in the Netherlands. From paid social to lifecycle marketing — integrated approach for sustainable growth.",
    h1: "Growth Marketing for D2C Brands: from first purchase to sustainable growth",
  },
  "/en/meta-ads-bureau-amsterdam": {
    title: "Meta Ads Agency Amsterdam for D2C E-commerce | Wrkt Digital",
    description:
      "Meta Ads agency in Amsterdam for D2C e-commerce. AI-driven Facebook and Instagram campaigns with proven creative frameworks.",
    h1: "Meta Ads for D2C E-commerce: advertising that scales",
  },
  "/en/tiktok-ads-bureau": {
    title: "TikTok Ads Agency for E-commerce | Wrkt Digital",
    description:
      "TikTok Ads agency for D2C e-commerce. AI-driven creative strategy, hook frameworks and campaign management for ambitious webshops.",
    h1: "TikTok Ads for E-commerce: the channel your competitors still underestimate",
  },
  "/en/ai-marketing-bureau": {
    title: "AI Marketing Agency for D2C E-commerce | Wrkt Digital",
    description:
      "AI marketing agency for D2C e-commerce. Proprietary AI tooling wrkt signal analyzes your market and optimizes campaigns 24/7.",
    h1: "AI marketing agency for D2C e-commerce",
  },
  "/en/signal": {
    title: "Wrkt Signal: Competitive Ad Intelligence for D2C | Wrkt Digital",
    description:
      "Wrkt Signal is Wrkt Digital's proprietary AI tool: hundreds of competitor ads per scan, classified by angle and format, translated into concrete testing opportunities.",
    h1: "Wrkt Signal: competitive ad intelligence for D2C e-commerce",
  },
  "/en/klaviyo-email-marketing-ecommerce": {
    title: "Klaviyo Agency for D2C E-commerce | Wrkt Digital",
    description:
      "Specialized in Klaviyo and email marketing for D2C e-commerce. Flows, segmentation and retention strategies that structurally increase revenue.",
    h1: "Klaviyo for D2C E-commerce: email marketing that drives revenue",
  },
};

/**
 * Resolve de route-key voor een pad + taal (client-side gebruik).
 * "/about" + "en" → "/en/about"; "/" + "en" → "/en".
 */
export function resolveRouteKey(path: string, lang: "nl" | "en"): string {
  const clean = path.replace(/\/$/, "") || "/";
  if (lang === "en" && !clean.startsWith("/en")) {
    return clean === "/" ? "/en" : `/en${clean}`;
  }
  return clean;
}


/**
 * Kort een tekst af tot een geldige meta description (≤155 tekens),
 * bij voorkeur op een zinsgrens. Gedeeld door server en client.
 */
export function metaDescription(text: string, max = 155): string {
  if (text.length <= max) return text;
  const cut = text.slice(0, max);
  const sentenceEnd = Math.max(cut.lastIndexOf(". "), cut.lastIndexOf("? "), cut.lastIndexOf("! "));
  // Alleen op zinsgrens knippen als het resultaat lang genoeg blijft (>=110 = Ahrefs-ondergrens).
  if (sentenceEnd >= 110) return cut.slice(0, sentenceEnd + 1);
  const wordEnd = cut.lastIndexOf(" ");
  return cut.slice(0, wordEnd) + "…";
}

/** Titelformaat voor artikelpagina's — gedeeld door server en client. */
export function articleTitle(name: string): string {
  return `${name} | Wrkt Digital`;
}

/** Titelformaat voor casepagina's — gedeeld door server en client. */
export function caseTitle(name: string): string {
  return `${name} | D2C Marketing Case - Wrkt Digital`;
}

/** Descriptionformaat voor casepagina's — gedeeld door server en client. */
export function caseDescription(name: string, lang: "nl" | "en"): string {
  return lang === "en"
    ? `${name} case study. Performance marketing results by Wrkt Digital, digital marketing agency Amsterdam.`
    : `${name} case study. Performance marketing resultaten door Wrkt Digital, digital marketing bureau Amsterdam.`;
}
